/**
 * Internal admin audit view for alarm notification deliveries.
 *
 * Issue: Djin-EMS-Software-Engineering/djin-cloud#265
 * Backed by: GET /api/v1/admin/notification-deliveries
 * RBAC:      platform_admin and parent_org_admin only (gated at the route
 *            via OPS_ADMIN_ROLES; backend re-checks via requireRole(...)).
 *
 * Notes for future maintainers:
 *   - The status enum has 6 values, not the 3 in the original issue body.
 *   - notification_deliveries.site_id is wire-format site_code (TEXT), not a
 *     UUID — pass it as a plain string in the site_id filter.
 *   - The alarm deep-link column is best-effort: row.device_id /
 *     row.condition_key come from a LEFT JOIN against alarm_history and may
 *     be null if alarm_history rows have been purged.
 *   - CSV export only exports the currently-loaded page (server caps limit
 *     at 500). Crank the limit and click Export for ad-hoc bulk pulls.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { Download, RefreshCw, ExternalLink } from 'lucide-react';
import { fetchNotificationDeliveries } from '../services/adminApi';
import {
  DELIVERY_CHANNELS,
  DELIVERY_STATUSES,
  type DeliveryChannel,
  type DeliveryStatus,
  type NotificationDeliveriesFilters,
  type NotificationDeliveryRow,
} from '../types/notificationDelivery';
import { exportToCSV } from '../utils/chartExport';

const DEFAULT_LIMIT = 50;

interface FilterState {
  start_date: string;
  end_date: string;
  channel: DeliveryChannel | '';
  status: DeliveryStatus | '';
  user_id: string;
  site_id: string;
  customer_id: string;
}

const EMPTY_FILTERS: FilterState = {
  start_date: '',
  end_date: '',
  channel: '',
  status: '',
  user_id: '',
  site_id: '',
  customer_id: '',
};

function statusPillClass(status: DeliveryStatus): string {
  switch (status) {
    case 'sent':
      return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    case 'failed':
      return 'bg-rose-50 text-rose-700 border-rose-200';
    case 'pending':
      return 'bg-amber-50 text-amber-700 border-amber-200';
    case 'suppressed_dedup':
    case 'suppressed_quiet_hours':
    case 'suppressed_stub':
      return 'bg-gray-100 text-gray-600 border-gray-200';
    default:
      return 'bg-gray-100 text-gray-600 border-gray-200';
  }
}

function channelPillClass(channel: DeliveryChannel): string {
  switch (channel) {
    case 'email':
      return 'bg-sky-50 text-sky-700 border-sky-200';
    case 'sms':
      return 'bg-violet-50 text-violet-700 border-violet-200';
    case 'in_app':
      return 'bg-slate-50 text-slate-700 border-slate-200';
    default:
      return 'bg-gray-100 text-gray-600 border-gray-200';
  }
}

function formatTs(ts: string): string {
  try {
    return format(new Date(ts), 'yyyy-MM-dd HH:mm:ss');
  } catch {
    return ts;
  }
}

function alarmHref(row: NotificationDeliveryRow): string | null {
  if (!row.device_id || !row.condition_key || !row.site_uuid) return null;
  // Only link when alarm_history context is available (non-null device/condition means
  // the LEFT JOIN matched). Navigates to the site's alarms tab — no deeper anchor yet.
  return `/project/${encodeURIComponent(row.site_uuid)}/alarms`;
}

export function AdminNotificationAudit() {
  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [rows, setRows] = useState<NotificationDeliveryRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadCounter, setReloadCounter] = useState(0);

  const apiFilters = useMemo<NotificationDeliveriesFilters>(() => {
    const out: NotificationDeliveriesFilters = { page, limit };
    if (filters.start_date) out.start_date = new Date(filters.start_date).toISOString();
    if (filters.end_date) out.end_date = new Date(filters.end_date).toISOString();
    if (filters.channel) out.channel = filters.channel;
    if (filters.status) out.status = filters.status;
    if (filters.user_id.trim()) out.user_id = filters.user_id.trim();
    if (filters.site_id.trim()) out.site_id = filters.site_id.trim();
    if (filters.customer_id.trim()) out.customer_id = filters.customer_id.trim();
    return out;
  }, [filters, page, limit]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchNotificationDeliveries(apiFilters)
      .then((res) => {
        if (cancelled) return;
        setRows(res.data.deliveries);
        setTotal(res.data.total);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : 'Failed to load deliveries';
        setError(msg);
        setRows([]);
        setTotal(0);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [apiFilters, reloadCounter]);

  const totalPages = Math.max(1, Math.ceil(total / limit));

  function updateFilter<K extends keyof FilterState>(key: K, value: FilterState[K]) {
    setFilters((f) => ({ ...f, [key]: value }));
    setPage(1);
  }

  function handleClearFilters() {
    setFilters(EMPTY_FILTERS);
    setPage(1);
  }

  function handleExport() {
    const exportRows = rows.map((r) => ({
      timestamp: r.ts,
      user_email: r.user_email ?? '',
      site_name: r.site_name ?? r.site_id,
      site_id: r.site_id,
      channel: r.channel,
      status: r.status,
      severity: r.severity ?? '',
      condition_label: r.condition_label ?? '',
      device_id: r.device_id ?? '',
      alarm_event_id: r.alarm_event_id,
      provider_msg_id: r.provider_msg_id ?? '',
      error: r.error ?? '',
    }));
    exportToCSV(exportRows, 'notification-deliveries', {
      timestamp: 'Timestamp',
      user_email: 'User',
      site_name: 'Site',
      site_id: 'Site Code',
      channel: 'Channel',
      status: 'Status',
      severity: 'Severity',
      condition_label: 'Condition',
      device_id: 'Device',
      alarm_event_id: 'Alarm Event ID',
      provider_msg_id: 'Provider Msg ID',
      error: 'Error',
    });
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-bold text-ems-text-primary">Notification Audit</h1>
          <p className="text-sm text-ems-text-muted">
            Internal delivery audit for alarm notifications. Alarm context columns (device,
            condition) are best-effort — they come from a LEFT JOIN against alarm_history and may
            be null for older rows.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setReloadCounter((c) => c + 1)}
            className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 hover:bg-gray-50"
            title="Reload"
            aria-label="Reload"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={handleExport}
            disabled={rows.length === 0}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-ems-forest text-ems-green border border-ems-green/20 text-sm font-medium hover:bg-ems-forest/80 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
        </div>
      </div>

      {/* Filter bar */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <label className="flex flex-col gap-1 text-xs text-gray-600">
          <span>Start (UTC)</span>
          <input
            type="datetime-local"
            value={filters.start_date}
            onChange={(e) => updateFilter('start_date', e.target.value)}
            className="px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-900 outline-none focus:border-ems-green focus:ring-2 focus:ring-ems-green/20"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs text-gray-600">
          <span>End (UTC)</span>
          <input
            type="datetime-local"
            value={filters.end_date}
            onChange={(e) => updateFilter('end_date', e.target.value)}
            className="px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-900 outline-none focus:border-ems-green focus:ring-2 focus:ring-ems-green/20"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs text-gray-600">
          <span>Channel</span>
          <select
            value={filters.channel}
            onChange={(e) => updateFilter('channel', e.target.value as FilterState['channel'])}
            className="px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-900 outline-none focus:border-ems-green"
          >
            <option value="">All channels</option>
            {DELIVERY_CHANNELS.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs text-gray-600">
          <span>Status</span>
          <select
            value={filters.status}
            onChange={(e) => updateFilter('status', e.target.value as FilterState['status'])}
            className="px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-900 outline-none focus:border-ems-green"
          >
            <option value="">All statuses</option>
            {DELIVERY_STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs text-gray-600">
          <span>User ID</span>
          <input
            type="text"
            placeholder="UUID"
            value={filters.user_id}
            onChange={(e) => updateFilter('user_id', e.target.value)}
            className="px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-900 outline-none focus:border-ems-green focus:ring-2 focus:ring-ems-green/20"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs text-gray-600">
          <span>Site code</span>
          <input
            type="text"
            placeholder="e.g. pod-hondo"
            value={filters.site_id}
            onChange={(e) => updateFilter('site_id', e.target.value)}
            className="px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-900 outline-none focus:border-ems-green focus:ring-2 focus:ring-ems-green/20"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs text-gray-600">
          <span>Customer ID (platform_admin only)</span>
          <input
            type="text"
            placeholder="UUID — leave blank for cross-tenant"
            value={filters.customer_id}
            onChange={(e) => updateFilter('customer_id', e.target.value)}
            className="px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-900 outline-none focus:border-ems-green focus:ring-2 focus:ring-ems-green/20"
          />
        </label>
        <div className="flex items-end gap-2">
          <button
            onClick={handleClearFilters}
            className="px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 hover:bg-gray-50"
          >
            Clear filters
          </button>
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="bg-rose-50 border border-rose-200 text-rose-700 text-sm rounded-lg px-4 py-3">
          {error}
        </div>
      )}

      {/* Results table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between text-xs text-gray-500">
          <span>
            {loading
              ? 'Loading…'
              : `${total.toLocaleString()} delivery row${total === 1 ? '' : 's'} match — showing page ${page} of ${totalPages}`}
          </span>
          <label className="flex items-center gap-2">
            <span>Rows per page</span>
            <select
              value={limit}
              onChange={(e) => {
                setLimit(parseInt(e.target.value, 10));
                setPage(1);
              }}
              className="px-2 py-1 rounded border border-gray-200 text-xs"
            >
              {[25, 50, 100, 250, 500].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
              <tr>
                <th className="px-4 py-2 text-left">Timestamp (UTC)</th>
                <th className="px-4 py-2 text-left">User</th>
                <th className="px-4 py-2 text-left">Site</th>
                <th className="px-4 py-2 text-left">Channel</th>
                <th className="px-4 py-2 text-left">Status</th>
                <th className="px-4 py-2 text-left">Severity</th>
                <th className="px-4 py-2 text-left">Condition</th>
                <th className="px-4 py-2 text-left">Error</th>
                <th className="px-4 py-2 text-left">Alarm</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.length === 0 && !loading ? (
                <tr>
                  <td colSpan={9} className="px-4 py-12 text-center text-gray-400 text-sm">
                    No delivery rows match these filters.
                  </td>
                </tr>
              ) : (
                rows.map((r) => {
                  const href = alarmHref(r);
                  return (
                    <tr key={r.delivery_id} className="hover:bg-gray-50">
                      <td className="px-4 py-2 font-mono text-xs text-gray-700 whitespace-nowrap">
                        {formatTs(r.ts)}
                      </td>
                      <td className="px-4 py-2 text-gray-900">
                        <div className="flex flex-col">
                          <span className="text-sm">{r.user_email ?? '—'}</span>
                          {(r.user_first_name || r.user_last_name) && (
                            <span className="text-xs text-gray-500">
                              {[r.user_first_name, r.user_last_name].filter(Boolean).join(' ')}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-2 text-gray-900">
                        <div className="flex flex-col">
                          <span className="text-sm">{r.site_name ?? r.site_id}</span>
                          <span className="text-xs text-gray-500 font-mono">{r.site_id}</span>
                        </div>
                      </td>
                      <td className="px-4 py-2">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full border text-xs font-medium ${channelPillClass(r.channel)}`}
                        >
                          {r.channel}
                        </span>
                      </td>
                      <td className="px-4 py-2">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full border text-xs font-medium ${statusPillClass(r.status)}`}
                        >
                          {r.status}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-gray-700 text-xs">{r.severity ?? '—'}</td>
                      <td className="px-4 py-2 text-gray-700 text-xs">
                        {r.condition_label ?? r.condition_key ?? '—'}
                      </td>
                      <td className="px-4 py-2 text-rose-700 text-xs max-w-xs truncate" title={r.error ?? ''}>
                        {r.error ?? '—'}
                      </td>
                      <td className="px-4 py-2 text-xs">
                        {href ? (
                          <Link
                            to={href}
                            className="inline-flex items-center gap-1 text-ems-green hover:underline"
                          >
                            <ExternalLink className="w-3 h-3" />
                            {r.alarm_event_id.slice(0, 8)}
                          </Link>
                        ) : (
                          <span className="text-gray-400 font-mono">{r.alarm_event_id.slice(0, 8)}</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {/* Paginator */}
        <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between text-xs text-gray-500">
          <span>
            Page {page} of {totalPages}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1 || loading}
              className="px-3 py-1.5 rounded border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages || loading}
              className="px-3 py-1.5 rounded border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
